/**
 * SettingsPage — every signed-in user can adjust their own profile +
 * preferences here. Phase 7 R16, item 11 from Rafael's R15 review.
 *
 * Tabs:
 *   - Profile     — name, avatar, password change, read-only email
 *   - Preferences — language, timezone, foot size unit
 *   - Paper forms - printable blank prescription PDFs for in-clinic hand
 *                   filling (generated from the live wizard option catalog)
 *
 * The Catalogues tab in the original spec is omitted from v1: it would
 * just be a list of links that already exist in the sidebar for admins.
 *
 * Avatar upload uses the `avatars` storage bucket (Phase 7 settings
 * migration). Per-user RLS: each user can write to a folder named
 * after their auth.uid().
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Button,
  Card,
  CardBody,
  Eyebrow,
  FormField,
  Heading,
  Input,
  Label,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@insolech/ui';
import { Camera, Save, KeyRound } from 'lucide-react';
import { profileQueries } from '@insolech/sdk';
import { AppShell } from '@/components/layout/AppShell';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { PaperFormsTab } from './paper-forms/PaperFormsTab';
import { EmailNotificationsTab } from './EmailNotificationsTab';

const TIMEZONES = [
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'America/New_York',
  'UTC',
] as const;

const LANGUAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'العربية' },
  { value: 'es', label: 'Español' },
];

const FOOT_SIZE_UNITS: ReadonlyArray<{ value: profileQueries.FootSizeUnit; label: string }> = [
  { value: 'eu', label: 'EU (canonical)' },
  { value: 'uk', label: 'UK' },
  { value: 'us', label: 'US' },
];

export function SettingsPage() {
  const { user, refreshProfile } = useAuth();
  const userId = user?.id ?? null;
  const qc = useQueryClient();

  const profile = useQuery(profileQueries.selfProfileQuery(supabase, userId));

  return (
    <AppShell>
      <main className="px-6 py-5 max-w-3xl mx-auto">
        <div className="mb-5">
          <Eyebrow>Settings</Eyebrow>
          <Heading as="h1" size="h3" className="mt-2">
            Your settings
          </Heading>
          <p className="text-text-secondary mt-1 text-sm">
            Profile, password, and preferences. Changes apply only to your account.
          </p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="paper-forms">Paper forms</TabsTrigger>
            <TabsTrigger value="notifications">Email notifications</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-5">
            {profile.isLoading ? (
              <Card>
                <CardBody>
                  <Skeleton className="h-6 w-40 mb-4" />
                  <Skeleton className="h-10 w-full mb-3" />
                  <Skeleton className="h-10 w-full" />
                </CardBody>
              </Card>
            ) : profile.data && userId ? (
              <ProfileTab
                profile={profile.data}
                userId={userId}
                onSaved={() => {
                  void qc.invalidateQueries({ queryKey: profileQueries.profileKeys.all });
                  void refreshProfile();
                }}
              />
            ) : (
              <Alert tone="error" title="Could not load profile">
                Reload the page; if it persists, contact support.
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="preferences" className="mt-5">
            {profile.data && userId ? (
              <PreferencesTab
                profile={profile.data}
                userId={userId}
                onSaved={() => {
                  void qc.invalidateQueries({ queryKey: profileQueries.profileKeys.all });
                  void refreshProfile();
                }}
              />
            ) : null}
          </TabsContent>

          <TabsContent value="paper-forms" className="mt-5">
            <PaperFormsTab />
          </TabsContent>

          <TabsContent value="notifications" className="mt-5">
            <EmailNotificationsTab />
          </TabsContent>
        </Tabs>
      </main>
    </AppShell>
  );
}

function ProfileTab({
  profile,
  userId,
  onSaved,
}: {
  profile: profileQueries.ProfileRow;
  userId: string;
  onSaved: () => void;
}) {
  const [fullName, setFullName] = React.useState(profile.full_name);
  const [phone, setPhone] = React.useState(profile.phone ?? '');
  const [avatarUrl, setAvatarUrl] = React.useState(profile.avatar_url);
  const [savedTick, setSavedTick] = React.useState(0);
  const fileInput = React.useRef<HTMLInputElement>(null);

  const saveProfile = useMutation({
    mutationFn: async () => {
      await profileQueries.updateSelfProfile(supabase, userId, {
        full_name: fullName.trim(),
        phone: phone.trim() || null,
      });
    },
    onSuccess: () => {
      setSavedTick((n) => n + 1);
      onSaved();
    },
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      const url = await profileQueries.uploadAvatar(supabase, userId, file);
      await profileQueries.updateSelfProfile(supabase, userId, { avatar_url: url });
      return url;
    },
    onSuccess: (url) => {
      setAvatarUrl(url);
      onSaved();
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardBody className="flex flex-col gap-5">
          {/* Avatar block */}
          <div className="flex items-center gap-4">
            <Avatar
              {...(avatarUrl ? { src: avatarUrl } : {})}
              name={fullName || profile.email}
              size="xl"
            />
            <div className="flex flex-col gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadAvatar.mutate(f);
                  e.target.value = '';
                }}
              />
              <Button
                variant="outline"
                size="sm"
                startIcon={<Camera className="h-4 w-4" />}
                onClick={() => fileInput.current?.click()}
                disabled={uploadAvatar.isPending}
              >
                {uploadAvatar.isPending ? 'Uploading…' : 'Change avatar'}
              </Button>
              <p className="text-xs text-text-muted">JPG / PNG / WebP, max 2 MB.</p>
            </div>
          </div>

          {uploadAvatar.isError ? (
            <Alert tone="error" title="Upload failed">
              {(uploadAvatar.error as Error)?.message ?? 'Try again with a smaller file.'}
            </Alert>
          ) : null}

          <FormField label="Full name" required>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your name"
            />
          </FormField>

          <FormField label="Email">
            <Input value={profile.email} disabled />
            <p className="mt-1 text-xs text-text-muted">
              Email cannot be changed here. Contact support if you need to.
            </p>
          </FormField>

          <FormField label="Phone">
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+971 50 123 4567"
            />
          </FormField>

          <div className="flex items-center gap-3">
            <Button
              size="md"
              startIcon={<Save className="h-4 w-4" />}
              onClick={() => saveProfile.mutate()}
              disabled={saveProfile.isPending || !fullName.trim()}
            >
              {saveProfile.isPending ? 'Saving…' : 'Save profile'}
            </Button>
            {savedTick > 0 && !saveProfile.isPending && !saveProfile.isError ? (
              <span className="text-sm text-success-700">Saved.</span>
            ) : null}
          </div>

          {saveProfile.isError ? (
            <Alert tone="error" title="Save failed">
              {(saveProfile.error as Error)?.message ?? 'Try again.'}
            </Alert>
          ) : null}
        </CardBody>
      </Card>

      <PasswordCard />
    </div>
  );
}

function PasswordCard() {
  const [pw1, setPw1] = React.useState('');
  const [pw2, setPw2] = React.useState('');
  const [savedTick, setSavedTick] = React.useState(0);

  const tooShort = pw1.length > 0 && pw1.length < 8;
  const mismatch = pw1.length > 0 && pw2.length > 0 && pw1 !== pw2;

  const change = useMutation({
    mutationFn: async () => {
      await profileQueries.updatePassword(supabase, pw1);
    },
    onSuccess: () => {
      setPw1('');
      setPw2('');
      setSavedTick((n) => n + 1);
    },
  });

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <div>
          <Label>Change password</Label>
          <p className="text-xs text-text-muted">Min 8 characters.</p>
        </div>
        <FormField label="New password">
          <Input
            type="password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            autoComplete="new-password"
          />
        </FormField>
        <FormField label="Confirm new password">
          <Input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            autoComplete="new-password"
          />
        </FormField>
        {tooShort ? (
          <p className="text-xs text-error-700">Password must be at least 8 characters.</p>
        ) : null}
        {mismatch ? (
          <p className="text-xs text-error-700">Passwords don&apos;t match.</p>
        ) : null}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="md"
            startIcon={<KeyRound className="h-4 w-4" />}
            onClick={() => change.mutate()}
            disabled={change.isPending || pw1.length < 8 || pw1 !== pw2}
          >
            {change.isPending ? 'Updating…' : 'Update password'}
          </Button>
          {savedTick > 0 && !change.isPending && !change.isError ? (
            <span className="text-sm text-success-700">Password updated.</span>
          ) : null}
        </div>
        {change.isError ? (
          <Alert tone="error" title="Could not update password">
            {(change.error as Error)?.message ?? 'Try again.'}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}

function PreferencesTab({
  profile,
  userId,
  onSaved,
}: {
  profile: profileQueries.ProfileRow;
  userId: string;
  onSaved: () => void;
}) {
  const [language, setLanguage] = React.useState(profile.language_preference);
  const [timezone, setTimezone] = React.useState(profile.timezone);
  const [unit, setUnit] = React.useState<profileQueries.FootSizeUnit>(profile.foot_size_unit);
  const [savedTick, setSavedTick] = React.useState(0);

  const save = useMutation({
    mutationFn: async () => {
      await profileQueries.updateSelfProfile(supabase, userId, {
        language_preference: language,
        timezone,
        foot_size_unit: unit,
      });
    },
    onSuccess: () => {
      setSavedTick((n) => n + 1);
      onSaved();
    },
  });

  return (
    <Card>
      <CardBody className="flex flex-col gap-5">
        <FormField label="Display language">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="h-10 w-full rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-muted">
            The language switcher in the header sets this for the current session.
            Saving here makes it persistent across sign-ins.
          </p>
        </FormField>

        <FormField label="Timezone">
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="h-10 w-full rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-muted">
            All timestamps in the app are rendered in this zone.
          </p>
        </FormField>

        <FormField label="Foot size unit">
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as profileQueries.FootSizeUnit)}
            className="h-10 w-full rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            {FOOT_SIZE_UNITS.map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-text-muted">
            EU is the canonical unit for OTS insoles. UK / US affect the size
            picker in the order wizard (rolling out).
          </p>
        </FormField>

        <div className="flex items-center gap-3">
          <Button
            size="md"
            startIcon={<Save className="h-4 w-4" />}
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save preferences'}
          </Button>
          {savedTick > 0 && !save.isPending && !save.isError ? (
            <span className="text-sm text-success-700">Saved.</span>
          ) : null}
        </div>

        {save.isError ? (
          <Alert tone="error" title="Save failed">
            {(save.error as Error)?.message ?? 'Try again.'}
          </Alert>
        ) : null}
      </CardBody>
    </Card>
  );
}
